import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Mail, RefreshCw, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationBanner = ({ user, onVerified, onStartOver }) => {
    const [resending, setResending] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const handleResendVerification = async () => {
        setResending(true);
        try {
            const token = localStorage.getItem('eskrybe_token');
            await axios.post(`${API_URL}/api/auth/resend-verification`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Verification email sent! Check your inbox.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send verification email');
        } finally {
            setResending(false);
        }
    };

    // Check URL for verification token on mount
    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const verifyToken = urlParams.get('verify');
        
        if (verifyToken) {
            verifyEmail(verifyToken);
        }
    }, []);

    const verifyEmail = async (token) => {
        setVerifying(true);
        try {
            await axios.post(`${API_URL}/api/auth/verify-email?token=${token}`);
            toast.success('Email verified successfully! Welcome to eSkrybe!');
            // Remove token from URL and redirect to home/dashboard
            window.history.replaceState({}, document.title, '/');
            // Trigger refresh to update user state
            if (onVerified) onVerified();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to verify email');
        } finally {
            setVerifying(false);
        }
    };

    if (user?.email_verified) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                        <Mail className="h-8 w-8 text-amber-600" />
                    </div>
                    <CardTitle className="font-serif text-2xl">Verify Your Email</CardTitle>
                    <CardDescription>
                        We've sent a verification link to <strong>{user?.email}</strong>
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {verifying ? (
                        <div className="flex items-center justify-center gap-2 py-4">
                            <RefreshCw className="h-5 w-5 animate-spin" />
                            <span>Verifying your email...</span>
                        </div>
                    ) : (
                        <>
                            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                                <div className="flex items-start gap-2">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                                    <p className="text-sm">Check your inbox for the verification email</p>
                                </div>
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                                    <p className="text-sm">Don't forget to check your spam folder</p>
                                </div>
                            </div>
                            
                            {/* Spam folder warning */}
                            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                                <p className="text-sm text-amber-800 font-medium mb-2">
                                    Can't find the email?
                                </p>
                                <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                                    <li>Check your <strong>Spam</strong> or <strong>Junk</strong> folder</li>
                                    <li>If found in spam, move it to your Inbox</li>
                                    <li>Add <strong>support@eskrybe.com</strong> to your contacts to receive future notices</li>
                                </ul>
                            </div>
                            
                            <Button 
                                variant="outline" 
                                className="w-full"
                                onClick={handleResendVerification}
                                disabled={resending}
                            >
                                {resending ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="h-4 w-4 mr-2" />
                                        Resend Verification Email
                                    </>
                                )}
                            </Button>
                            
                            <p className="text-xs text-center text-muted-foreground">
                                You must verify your email to access eSkrybe features.
                                The verification link expires in 24 hours.
                            </p>
                            
                            {/* Start Over / Different Email Option */}
                            <div className="pt-2 border-t">
                                <Button 
                                    variant="ghost" 
                                    className="w-full text-muted-foreground hover:text-foreground"
                                    onClick={onStartOver}
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Use a Different Email
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default EmailVerificationBanner;
